import { ApiError, type Article, type ArticleAction, type ArticleRequest } from "@brandex/api-client";
import type { EditorChange, EditorLabels, JSONContent } from "@brandex/editor";
// A light entry point: the editor package itself (Tiptap) is loaded only when an article opens.
import { absolutizeMediaUrls } from "@brandex/editor/html";
import { formatRelative, type MessageKey } from "@brandex/i18n";
import { Alert, Badge, Button, Field, Input, Select, Spinner, Textarea } from "@brandex/ui";
import { ArrowLeft, CheckCircle2, Circle, ExternalLink, ImagePlus, Search, Trash2 } from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { useBrandex, useT } from "../context";
import { errorField, errorMessage } from "../errors";
import { mediaUrl, useArticle, useArticleActions, usePractice, websiteUrl } from "../queries";

/** The editor (Tiptap) is large: load it only when an article is opened. */
const ArticleEditor = lazy(() => import("@brandex/editor").then((m) => ({ default: m.ArticleEditor })));

/** Quiet time before a draft is saved on its own. */
export const AUTOSAVE_MS = 2000;

const TITLE_MIN = 5;
const SUMMARY_MAX = 300;
const TAGS_MAX = 5;
const FIELDS = ["title", "slug", "excerpt", "tags", "cover", "seo_title", "seo_description"];

type T = (key: MessageKey, vars?: Record<string, string | number>) => string;

/** Words a long-form article should have before it goes out (a guide, not a rule). */
export const GOOD_WORDS = 300;

/** Words in an editor document, counted like the editor's own counter (whitespace-separated). */
export function countWords(doc: JSONContent | null | undefined): number {
  if (!doc) return 0;
  const parts: string[] = [];
  const walk = (n: JSONContent) => {
    if (typeof n.text === "string") parts.push(n.text);
    // Block boundaries separate words even without spaces.
    if (n.content) {
      for (const c of n.content) walk(c);
      parts.push(" ");
    }
  };
  walk(doc);
  return parts.join("").split(/\s+/).filter(Boolean).length;
}

/** What a good article has before it is sent or published. */
export function readiness(a: { title: string; excerpt: string; cover: boolean; words: number; tags: number; seoDescription: string }) {
  return [
    { key: "articles.ready.title", done: a.title.trim().length >= TITLE_MIN },
    { key: "articles.ready.summary", done: a.excerpt.trim().length >= 50 },
    { key: "articles.ready.cover", done: a.cover },
    { key: "articles.ready.length", done: a.words >= GOOD_WORDS },
    { key: "articles.ready.tags", done: a.tags > 0 },
    { key: "articles.ready.seo", done: a.seoDescription.trim().length >= 50 || a.excerpt.trim().length >= 50 },
  ] as const;
}

/** Every string of the editor, translated. */
export function editorLabels(t: T): EditorLabels {
  return {
    toolbar: t("editor.toolbar"),
    undo: t("editor.undo"),
    redo: t("editor.redo"),
    heading2: t("editor.heading2"),
    heading3: t("editor.heading3"),
    heading4: t("editor.heading4"),
    paragraph: t("editor.paragraph"),
    bold: t("editor.bold"),
    italic: t("editor.italic"),
    underline: t("editor.underline"),
    strike: t("editor.strike"),
    code: t("editor.code"),
    link: t("editor.link"),
    removeLink: t("editor.removeLink"),
    linkAddress: t("editor.linkAddress"),
    linkApply: t("editor.linkApply"),
    linkInvalid: t("editor.linkInvalid"),
    bulletList: t("editor.bulletList"),
    orderedList: t("editor.orderedList"),
    quote: t("editor.quote"),
    codeBlock: t("editor.codeBlock"),
    rule: t("editor.rule"),
    image: t("editor.image"),
    imageUploading: t("editor.imageUploading"),
    imageFailed: t("editor.imageFailed"),
    imageAltPrompt: t("editor.imageAltPrompt"),
    table: t("editor.table"),
    addRow: t("editor.addRow"),
    addColumn: t("editor.addColumn"),
    deleteRow: t("editor.deleteRow"),
    deleteColumn: t("editor.deleteColumn"),
    deleteTable: t("editor.deleteTable"),
    embed: t("editor.embed"),
    embedSource: t("editor.embedSource"),
    embedHint: t("editor.embedHint"),
    embedTitle: t("editor.embedTitle"),
    embedAspect: t("editor.embedAspect"),
    embedInsert: t("editor.embedInsert"),
    embedCancel: t("editor.embedCancel"),
    embedInvalid: t("editor.embedInvalid"),
    aspects: {
      "16:9": t("editor.aspect.wide"),
      "4:3": t("editor.aspect.standard"),
      "1:1": t("editor.aspect.square"),
      "9:16": t("editor.aspect.tall"),
      "3:4": t("editor.aspect.portrait"),
    },
    words: (n) => t("editor.words", { n }),
    content: t("editor.content"),
  };
}

/** A web address made from a title; empty when there is nothing usable (Bangla titles). */
export function slugify(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80)
    .replace(/-+$/g, "");
  return slug.length >= 3 ? slug : "";
}

export interface WorkflowAction {
  action: ArticleAction;
  label: MessageKey;
  primary: boolean;
}

/**
 * What the person may do next. Only the doctor publishes; everyone else
 * sends the article for review (ADR 0019).
 */
export function workflowActions(status: Article["status"], canPublish: boolean): WorkflowAction[] {
  switch (status) {
    case "draft":
      return canPublish
        ? [{ action: "publish", label: "articles.action.publish", primary: true }]
        : [{ action: "submit", label: "articles.action.submit", primary: true }];
    case "in_review":
      return canPublish
        ? [
            { action: "publish", label: "articles.action.publish", primary: true },
            { action: "request-changes", label: "articles.action.requestChanges", primary: false },
          ]
        : [];
    case "published":
      return [{ action: "unpublish", label: "articles.action.unpublish", primary: false }];
    case "unpublished":
      return canPublish
        ? [{ action: "publish", label: "articles.action.publishAgain", primary: true }]
        : [{ action: "submit", label: "articles.action.submit", primary: true }];
  }
}

function parseTags(raw: string): string[] {
  return raw
    .split(/[,،]/)
    .map((x) => x.trim())
    .filter(Boolean);
}

/**
 * The server's validation messages ("body write at least 50 words") say what
 * to fix, so they are shown as they are; the shared helper would reduce them
 * to "check the highlighted field", which is no help when the field is the
 * article text or a publish rule.
 */
function articleError(error: unknown, t: T): string {
  return error instanceof ApiError && error.status === 422 ? error.message : errorMessage(error, t);
}

type SaveState = "idle" | "saving" | "saved" | "error";

interface Cover {
  key: string;
  url: string;
}

export interface ArticleEditorPageProps {
  orgId: string;
  /** The article to edit; `null` starts a new one. */
  id: string | null;
  onClose: () => void;
  /** Called once a new article got its id (to put it in the address bar). */
  onCreated?: (id: string) => void;
  autosaveMs?: number;
}

/** Loads the article, then hands it to the form (which owns all editing state). */
export function ArticleEditorPage({ id, ...rest }: ArticleEditorPageProps) {
  const t = useT();
  const loaded = useArticle(rest.orgId, id ?? undefined);
  if (id === null) return <ArticleForm article={null} {...rest} />;
  if (loaded.isPending) return <Spinner label={t("common.loading")} />;
  if (loaded.isError) {
    return (
      <div className="grid gap-4">
        <Alert tone="error">{errorMessage(loaded.error, t)}</Alert>
        <div>
          <Button variant="outline" onClick={rest.onClose}>
            {t("articles.back")}
          </Button>
        </div>
      </div>
    );
  }
  return <ArticleForm article={loaded.data} {...rest} />;
}

function ArticleForm({
  article: initial,
  orgId,
  onClose,
  onCreated,
  autosaveMs = AUTOSAVE_MS,
}: Omit<ArticleEditorPageProps, "id"> & { article: Article | null }) {
  const t = useT();
  const { locale, apiBaseUrl, siteUrl } = useBrandex();
  const practice = usePractice(orgId);
  const actions = useArticleActions(orgId);
  const actionsRef = useRef(actions);

  // What the server has.
  const [server, setServer] = useState<Article | null>(initial);
  const serverRef = useRef<Article | null>(initial);

  // What the person is editing.
  const [title, setTitle] = useState(initial?.title ?? "");
  const [slug, setSlug] = useState(initial?.slug ?? "");
  const slugTouched = useRef(initial !== null);
  const [excerpt, setExcerpt] = useState(initial?.excerpt ?? "");
  const [lang, setLang] = useState<"en" | "bn">(initial?.locale ?? locale);
  const [tags, setTags] = useState((initial?.tags ?? []).join(", "));
  const [cover, setCover] = useState<Cover | null>(initial?.cover ?? null);
  const [seoTitle, setSeoTitle] = useState(initial?.seo_title ?? "");
  const [seoDescription, setSeoDescription] = useState(initial?.seo_description ?? "");
  const [initialDoc] = useState<JSONContent | null>(() => (initial?.body as JSONContent | null | undefined) ?? null);
  const [words, setWords] = useState(() => countWords(initialDoc));
  const doc = useRef<JSONContent | null>(initialDoc);
  const empty = useRef(initialDoc === null);

  // Change tracking: `rev` counts edits, `savedRev` is the edit the server has.
  const [rev, setRev] = useState(0);
  const [savedRev, setSavedRev] = useState(0);
  const revRef = useRef(0);
  const savedRevRef = useRef(0);
  const touch = () => {
    revRef.current += 1;
    setRev(revRef.current);
  };
  const dirty = rev !== savedRev;

  const [state, setState] = useState<SaveState>("idle");
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [tab, setTab] = useState<"write" | "preview">("write");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverError, setCoverError] = useState(false);
  const coverInput = useRef<HTMLInputElement>(null);

  const build = (): ArticleRequest => {
    const body: ArticleRequest = {
      title,
      locale: lang,
      excerpt,
      tags: parseTags(tags),
      cover: cover?.key ?? null,
      seo_title: seoTitle,
      seo_description: seoDescription,
      body: empty.current ? null : (doc.current as ArticleRequest["body"]),
    };
    const wanted = slug.trim();
    if (wanted !== "") body.slug = wanted;
    return body;
  };
  const buildRef = useRef(build);
  // Async callbacks must see the latest values; refs are updated after each render.
  useEffect(() => {
    actionsRef.current = actions;
    buildRef.current = build;
  });

  // One save at a time; edits made meanwhile are saved right after.
  const running = useRef<Promise<void> | null>(null);
  const queued = useRef(false);
  const hasQueued = () => queued.current;
  const saveNow = useCallback((): Promise<void> => {
    if (running.current !== null) {
      queued.current = true;
      return running.current;
    }
    const run = async () => {
      do {
        queued.current = false;
        const startRev = revRef.current;
        setState("saving");
        try {
          const created = serverRef.current === null;
          const saved = await actionsRef.current.save(serverRef.current?.id ?? null, buildRef.current());
          serverRef.current = saved;
          setServer(saved);
          savedRevRef.current = startRev;
          setSavedRev(startRev);
          setSavedAt(new Date().toISOString());
          setError(null);
          setState("saved");
          if (!slugTouched.current) setSlug(saved.slug);
          if (created) onCreated?.(saved.id);
        } catch (e) {
          setError(e);
          setState("error");
          queued.current = false;
          throw e;
        }
      } while (hasQueued());
    };
    const p = run().finally(() => {
      running.current = null;
    });
    running.current = p;
    return p;
  }, [onCreated]);

  // Autosave drafts. A live article is saved only on request, so readers
  // never see half-written text.
  useEffect(() => {
    if (rev === savedRevRef.current) return;
    if (serverRef.current?.status === "published") return;
    if (serverRef.current === null && title.trim().length < TITLE_MIN) return;
    const timer = setTimeout(() => {
      saveNow().catch(() => undefined);
    }, autosaveMs);
    return () => clearTimeout(timer);
  }, [rev, title, autosaveMs, saveNow]);

  // Leaving with unsaved text asks first.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  /** Saves what is pending. Resolves `false` (and shows the error) when that fails. */
  const ensureSaved = async (): Promise<boolean> => {
    if (revRef.current === savedRevRef.current && serverRef.current !== null) return true;
    if (serverRef.current === null && title.trim().length < TITLE_MIN) {
      setError(new Error("title"));
      setState("error");
      return false;
    }
    try {
      await saveNow();
      return true;
    } catch {
      return false;
    }
  };

  const act = async (action: ArticleAction) => {
    setBusy(true);
    try {
      if (!(await ensureSaved())) return;
      const current = serverRef.current;
      if (current === null) return;
      const next = await actionsRef.current.act(current.id, action);
      serverRef.current = next;
      setServer(next);
      setError(null);
      setState("saved");
    } catch (e) {
      setError(e);
      setState("error");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const current = serverRef.current;
    if (current === null) return;
    setBusy(true);
    try {
      await actionsRef.current.remove(current.id);
      savedRevRef.current = revRef.current;
      onClose();
    } catch (e) {
      setError(e);
      setState("error");
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    if (dirty && !(await ensureSaved())) return;
    onClose();
  };

  const showPreview = async () => {
    if (dirty && !(await ensureSaved())) return;
    setTab("preview");
  };

  const pickCover = async (file: File) => {
    setCoverBusy(true);
    setCoverError(false);
    try {
      const img = await actionsRef.current.uploadImage("cover", file);
      setCover({ key: img.key, url: img.url });
      touch();
    } catch {
      setCoverError(true);
    } finally {
      setCoverBusy(false);
    }
  };

  const onEditor = useCallback((change: EditorChange) => {
    doc.current = change.doc;
    empty.current = change.empty;
    setWords(change.words);
    revRef.current += 1;
    setRev(revRef.current);
  }, []);
  const uploadInline = useCallback(async (file: File) => {
    const img = await actionsRef.current.uploadImage("inline", file);
    return { url: img.url };
  }, []);
  const displayUrl = useCallback((stored: string) => mediaUrl(apiBaseUrl, stored), [apiBaseUrl]);
  const labels = editorLabels(t);
  const ready = readiness({ title, excerpt, cover: cover !== null, words, tags: parseTags(tags).length, seoDescription });

  const status = server?.status ?? "draft";
  const canPublish = server?.can_publish ?? true;
  const live = status === "published";
  const field = errorField(error);
  const code = error instanceof ApiError ? error.code : undefined;
  const fieldError = (name: string): string | undefined => (field === name ? articleError(error, t) : undefined);
  const slugError = code === "article_slug" ? t("articles.error.slugTaken") : fieldError("slug");
  const titleFirst = error instanceof Error && error.message === "title";
  const pointsAtField = code === "article_slug" || (field !== undefined && FIELDS.includes(field));
  const generalError = error !== null && !titleFirst && !pointsAtField ? articleError(error, t) : null;
  const website = practice.data?.website;
  const liveUrl = website ? `${websiteUrl(website, siteUrl)}/blog/${server?.slug ?? slug}` : null;
  const workflow = workflowActions(status, canPublish);

  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => void close()} disabled={busy}>
          <ArrowLeft className="h-4 w-4" aria-hidden />
          {t("articles.back")}
        </Button>
        <Badge tone={status === "published" ? "success" : status === "in_review" ? "accent" : "neutral"}>
          {t(`articles.status.${status}` as MessageKey)}
        </Badge>
        <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
          {state === "saving"
            ? t("articles.saving")
            : dirty
              ? t("articles.unsaved")
              : savedAt !== null
                ? t("articles.saved", { when: formatRelative(locale, savedAt) })
                : ""}
        </p>
        <div className="ms-auto flex flex-wrap items-center gap-2">
          {live && liveUrl !== null ? (
            <Button asChild variant="outline" size="sm">
              <a href={liveUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" aria-hidden />
                {t("articles.viewLive")}
              </a>
            </Button>
          ) : null}
          <Button variant="outline" size="sm" loading={state === "saving"} disabled={busy || (!dirty && server !== null)} onClick={() => void ensureSaved()}>
            {live ? t("articles.saveChanges") : t("articles.saveDraft")}
          </Button>
          {workflow.map((w) => (
            <Button key={w.action} variant={w.primary ? "primary" : "outline"} size="sm" loading={busy} disabled={busy} onClick={() => void act(w.action)}>
              {t(w.label)}
            </Button>
          ))}
        </div>
      </div>

      {titleFirst ? <Alert tone="error">{t("articles.error.titleFirst")}</Alert> : null}
      {generalError !== null ? <Alert tone="error">{state === "error" && !dirty ? generalError : t("articles.saveFailed")}</Alert> : null}
      {live && canPublish ? <Alert>{t("articles.liveNote")}</Alert> : null}
      {!canPublish ? <Alert>{status === "in_review" ? t("articles.waitingDoctor") : t("articles.drafterNote")}</Alert> : null}
      {live ? <Alert tone="success">{t("articles.live")}</Alert> : null}

      <div role="tablist" aria-label={t("articles.title")} className="flex gap-1 border-b border-border">
        {(["write", "preview"] as const).map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            id={`article-tab-${k}`}
            aria-selected={tab === k}
            aria-controls={`article-panel-${k}`}
            onClick={() => (k === "write" ? setTab("write") : void showPreview())}
            className="-mb-px border-b-2 border-transparent px-4 py-2 text-sm text-muted-foreground hover:text-foreground aria-selected:border-primary aria-selected:font-medium aria-selected:text-foreground"
          >
            {t(k === "write" ? "articles.write" : "articles.preview")}
          </button>
        ))}
      </div>

      <div role="tabpanel" id="article-panel-write" aria-labelledby="article-tab-write" hidden={tab !== "write"} className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem] xl:items-start">
        <div className="grid min-w-0 gap-5">
          <Field label={t("articles.field.title")} hint={t("articles.field.titleHint")} error={fieldError("title")}>
            <Input
              value={title}
              maxLength={120}
              className="h-14 font-display text-2xl font-bold md:text-3xl"
              lang={lang}
              onChange={(e) => {
                setTitle(e.target.value);
                if (!slugTouched.current) setSlug(slugify(e.target.value));
                touch();
              }}
            />
          </Field>
          <Field label={t("articles.field.excerpt")} hint={t("articles.field.excerptHint", { n: excerpt.length })} error={fieldError("excerpt")}>
            <Textarea
              value={excerpt}
              rows={3}
              maxLength={SUMMARY_MAX}
              lang={lang}
              onChange={(e) => {
                setExcerpt(e.target.value);
                touch();
              }}
            />
          </Field>
          <div className="grid gap-2">
            <p id="article-body-label" className="text-sm font-medium">
              {t("articles.body")}
            </p>
            <Suspense fallback={<Spinner label={t("common.loading")} />}>
              <ArticleEditor
                initialContent={initialDoc}
                onChange={onEditor}
                uploadImage={uploadInline}
                displayUrl={displayUrl}
                labels={labels}
                placeholder={t("articles.bodyPlaceholder")}
                lang={lang}
                labelledBy="article-body-label"
                disabled={busy}
              />
            </Suspense>
            {fieldError("body") !== undefined ? <p role="alert" className="text-xs font-medium text-destructive">{fieldError("body")}</p> : null}
          </div>
        </div>

        <aside aria-label={t("articles.settings")} className="grid gap-4 xl:sticky xl:top-4">
          <SideCard title={t("articles.ready")}>
            <ul className="grid gap-2 text-sm">
              {ready.map((r) => (
                <li key={r.key} className="flex items-center gap-2">
                  {r.done ? <CheckCircle2 className="h-4 w-4 shrink-0 text-mint-500" aria-hidden /> : <Circle className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />}
                  <span className={r.done ? "" : "text-muted-foreground"}>{t(r.key, { n: GOOD_WORDS })}</span>
                  <span className="sr-only">{r.done ? t("articles.ready.done") : t("articles.ready.todo")}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">{t("articles.ready.words", { n: words, minutes: Math.max(1, Math.ceil(words / 200)) })}</p>
          </SideCard>

          <SideCard title={t("articles.settings")}>
            <div className="grid gap-4">
              <Field label={t("articles.field.slug")} hint={liveUrl !== null ? `${liveUrl.replace(/[^/]*$/, "")}${slug || "…"}` : t("articles.field.slugHint")} error={slugError}>
                <Input
                  value={slug}
                  maxLength={80}
                  autoCapitalize="none"
                  spellCheck={false}
                  onChange={(e) => {
                    slugTouched.current = true;
                    setSlug(e.target.value.toLowerCase());
                    touch();
                  }}
                />
              </Field>
              <Field label={t("articles.field.language")}>
                <Select
                  value={lang}
                  onChange={(e) => {
                    setLang(e.target.value === "bn" ? "bn" : "en");
                    touch();
                  }}
                >
                  <option value="en">English</option>
                  <option value="bn">বাংলা</option>
                </Select>
              </Field>
            </div>
            <Field label={t("articles.field.tags")} hint={t("articles.field.tagsHint")} error={fieldError("tags") ?? (parseTags(tags).length > TAGS_MAX ? t("articles.field.tagsHint") : undefined)}>
              <Input
                value={tags}
                onChange={(e) => {
                  setTags(e.target.value);
                  touch();
                }}
              />
            </Field>
          </SideCard>

          <SideCard>
            <div className="grid gap-3">
              <p className="text-sm font-medium">{t("articles.field.cover")}</p>
              {cover !== null ? <img src={mediaUrl(apiBaseUrl, cover.url)} alt="" className="aspect-[16/9] w-full rounded-md object-cover" /> : null}
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" loading={coverBusy} onClick={() => coverInput.current?.click()}>
                  <ImagePlus className="h-4 w-4" aria-hidden />
                  {cover !== null ? t("articles.cover.replace") : t("articles.cover.upload")}
                </Button>
                {cover !== null ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCover(null);
                      touch();
                    }}
                  >
                    {t("articles.cover.remove")}
                  </Button>
                ) : null}
                <input
                  ref={coverInput}
                  type="file"
                  hidden
                  data-testid="cover-input"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) void pickCover(f);
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground">{t("articles.field.coverHint")}</p>
              {coverError || fieldError("cover") !== undefined ? <p role="alert" className="text-xs font-medium text-destructive">{fieldError("cover") ?? t("articles.error.upload")}</p> : null}
            </div>
          </SideCard>

          <SideCard>
            <div className="grid gap-1 rounded-md border border-border bg-background p-3" aria-label={t("articles.seo.preview")}>
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Search className="h-3.5 w-3.5" aria-hidden />
                {t("articles.seo.preview")}
              </p>
              <p className="truncate text-xs text-mint-700 dark:text-mint-400">{liveUrl !== null ? `${liveUrl.replace(/[^/]*$/, "")}${slug || "…"}` : slug || "…"}</p>
              <p className="line-clamp-2 font-medium text-primary">{(seoTitle.trim() || title.trim() || t("articles.untitled")).slice(0, 70)}</p>
              <p className="line-clamp-3 text-xs text-muted-foreground">{(seoDescription.trim() || excerpt.trim() || t("articles.seo.descriptionHint")).slice(0, 160)}</p>
            </div>
            <details className="group">
              <summary className="cursor-pointer text-sm font-medium">{t("articles.seo")}</summary>
              <div className="mt-4 grid gap-4">
                <Field label={t("articles.seo.title")} hint={t("articles.seo.titleHint")} error={fieldError("seo_title")}>
                  <Input
                    value={seoTitle}
                    maxLength={70}
                    onChange={(e) => {
                      setSeoTitle(e.target.value);
                      touch();
                    }}
                  />
                </Field>
                <Field label={t("articles.seo.description")} hint={t("articles.seo.descriptionHint")} error={fieldError("seo_description")}>
                  <Textarea
                    value={seoDescription}
                    rows={2}
                    maxLength={160}
                    onChange={(e) => {
                      setSeoDescription(e.target.value);
                      touch();
                    }}
                  />
                </Field>
              </div>
            </details>
          </SideCard>

          {server !== null && !live ? (
            <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
              {confirmDelete ? (
                <>
                  <p className="text-sm">{t("articles.action.deleteConfirm")}</p>
                  <Button variant="destructive" size="sm" loading={busy} onClick={() => void remove()}>
                    {t("articles.action.deleteYes")}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                    {t("articles.action.keep")}
                  </Button>
                </>
              ) : (
                <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="h-4 w-4" aria-hidden />
                  {t("articles.action.delete")}
                </Button>
              )}
            </div>
          ) : null}
        </aside>
      </div>

      <div role="tabpanel" id="article-panel-preview" aria-labelledby="article-tab-preview" hidden={tab !== "preview"}>
        {tab === "preview" ? (
          <article lang={lang} className="mx-auto grid max-w-3xl gap-5 rounded-[var(--radius-lg)] border border-border bg-card p-6">
            {cover !== null ? <img src={mediaUrl(apiBaseUrl, cover.url)} alt="" className="max-h-72 w-full rounded-md object-cover" /> : null}
            <h1 className="text-3xl font-bold leading-tight">{server?.title ?? title}</h1>
            {(server?.excerpt ?? excerpt) !== "" ? <p className="text-lg text-muted-foreground">{server?.excerpt ?? excerpt}</p> : null}
            {server !== null && server.body_html !== "" ? (
              // The server built this HTML from an allowlist (ADR 0019): text is escaped and only known tags are emitted.
              <div className="bx-prose" dangerouslySetInnerHTML={{ __html: absolutizeMediaUrls(server.body_html, apiBaseUrl) }} />
            ) : (
              <p className="text-sm text-muted-foreground">{t("articles.previewEmpty")}</p>
            )}
          </article>
        ) : null}
      </div>
    </div>
  );
}

function SideCard({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="grid gap-3 rounded-[var(--radius-lg)] border border-border bg-card p-4">
      {title !== undefined ? <h2 className="text-sm font-semibold">{title}</h2> : null}
      {children}
    </section>
  );
}
